<script module lang="ts">
  import { PersistedState } from 'runed';

  type Period = string;

  class Tabs<T extends string> {
    tabs = $state<T[]>([]);
    selected;
    scrollPosition = $state<Record<Period, number>>({});
    direction = $state(false);

    constructor(key: string, tab: T) {
      this.selected = new PersistedState<T>(key, tab, {
        serializer: getValueSerializer<T>(),
      });
    }

    register = (tab: T) => {
      this.tabs.push(tab);
      this.selected = this.selected ?? tab;

      onDestroy(() => {
        const i = this.tabs.indexOf(tab);
        this.tabs.splice(i, 1);
      });
    };

    select = async (tab: T) => {
      this.direction =
        this.tabs.indexOf(tab) > this.tabs.indexOf(this.selected.current);
      await tick();
      this.selected.current = tab;
    };
  }

  const TABS = Symbol('tabs');
  export const getTabContext = <T extends string>() =>
    getContext<Tabs<T>>(TABS);
</script>

<script lang="ts" generics="T extends string">
  import {
    setContext,
    onDestroy,
    getContext,
    tick,
    type Snippet,
    untrack,
  } from 'svelte';
  import { getValueSerializer } from '~/store/valueSerializer';

  type Props = {
    selected: { current: T };
    key: string;
    children: Snippet<[T]>;
  };
  let { selected = $bindable(), key, children }: Props = $props();
  const tabs = new Tabs<T>(key, selected.current);
  // synchronize
  $effect(() => {
    const t = selected;
    untrack(() => {
      if (tabs.selected !== t) tabs.select(t.current);
    });
  });
  $effect(() => {
    const t = tabs.selected.current;
    untrack(() => {
      selected.current = t;
    });
  });

  setContext<Tabs<T>>(TABS, tabs);
</script>

{@render children(tabs.selected.current)}
